import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { titleize, unescapeHTML } from 'underscore.string';
import { withTranslation, Translation } from 'react-i18next';
import {
  Loader2,
  Trash2,
  Plus,
  Download,
  MoreHorizontal,
  ChevronDown,
} from 'lucide-react';
import PaginationV2 from 'components/v2/PaginationV2';
import _ from 'underscore';
import { cloneDeep } from 'lodash';

import {
  Button as UiButton,
  Table as UiTable,
  TableHeader as UiTableHeader,
  TableBody as UiTableBody,
  TableRow as UiTableRow,
  TableHead as UiTableHead,
  TableCell as UiTableCell,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  BadgeAlt,
} from '@cargomatic/ui';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import CargomaticBadge from 'components/v2/Badge';
import { massUpdateActions } from 'constants/MassUpdates';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import OrderShipmentsStore from 'stores/OrderShipmentsStore';
import { updateOrder } from 'utils/OrderWebAPIUtils';

import EditEquipmentModal from 'components/modules/equipment/EditEquipmentModal';
import SendDOEmailModal from 'components/modules/shipments/SendDOEmailModal';
import BulkEditModal from 'components/modules/shipments/MassUpdateModal';
import BulkUpdateSendDOModal from 'components/modules/shipments/BulkUpdateSendDOModal';
import BulkUpdateCarrierCostModal from 'components/modules/shipments/BulkUpdateCarrierCostModal';
import ChangeUserModal from 'components/modules/shipments/ChangeUserModal';
import SessionStore from 'stores/SessionStore';
import TripFormStore from 'stores/TripFormStore';
import { Disclosure } from '@headlessui/react';
import { createShipment } from './CreateShipment';
import ShipmentStatusBadge from 'components/v2/ShipmentStatusBadge';
import BillingStatusBadge from 'components/v2/BillingStatusBadge';
import ManageStagingItem from './ManageStagingItem';
import RemoveShipmentModal from './RemoveShipmentModal';
import { isIntermodalShipmentType } from '../../../helpers/shipment-types';
import TripPlannerModal from '../shipments/TripPlannerModal';
import GenerateTripModal from '../shipments/trips/GenerateTripModal';
import { getDeliveryOrderRecipients } from '../../../helpers/shipments';
import BulkBookingForm from '../booking/BulkBookingForm';
import { getDocumentsV2, getSignedUrl } from '../../../utils/DocumentsAPIUtils';
import { openSignedUrlInNewTab } from '../../../utils/documents';
import { showToast } from '../../../stores/ToastStore';
import ConfirmAssignCarrierModal from '../shipments/ConfirmAssignCarrierModal';

const mapStagingShipmentItem = (shipment) => ({
  ...shipment,
  deliveryOrderGenerating: false,
  editRow: false,
  expanded: false,
});

const mapShipmentItem = (shipment) => ({
  ...shipment,
  deliveryOrderGenerating: false,
  editRow: false,
  expanded: false,
});

class ShipmentsList extends Component {
  static propTypes = {
    order: PropTypes.object,
    renderedInModal: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      accountingHold: false,
      showSendEmailModal: false,
      showTripPlannerModal: false,
      showGenerateTripModal: false,
      showBulkBookingFormModal: false,
      showRemoveModal: false,
      showEditEquipmentModal: false,
      selectedShipment: null,
      selectedEquipment: null,
      bookedShipmentsLoaded: false,
      stagingShipmentsLoaded: false,
      expanded: [],
      selected: [],
      selectAll: false,
      stagingShipments: props.stagingShipments,
      shipments: [],
      bookedShipments: props.bookedShipments,
      disableCreateShipment: false,
      paginationData: props.paginationData,
    };
  }

  componentDidMount() {
    OrderShipmentsStore.addShipmentCreationListener(
      this.handleOrderShipmentCreation,
    );
    TripFormStore.addTripAppliedEventListener(this.handleTripAppliedEvent);

    this.handleOrderShipmentsStoreChange();
    this.handleStagingShipmentsChange();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const accountingHold =
      nextProps.order.shipper && nextProps.order.shipper.accounting
        ? nextProps.order.shipper.accounting.on_hold
        : false;
    this.setState({ accountingHold });
    if (this.props.order._id !== nextProps.order._id) {
      this.props.onGetPaginatedShipments(1);
      this.handleStagingShipmentsChange(nextProps.stagingShipments);
    }

    if (this.props.bookedShipments !== nextProps.bookedShipments) {
      this.updateShipmentsState(nextProps.bookedShipments);
    }

    if (this.props.stagingShipments !== nextProps.stagingShipments) {
      this.handleStagingShipmentsChange(nextProps.stagingShipments);
    }

    if (this.props.paginationData !== nextProps.paginationData) {
      this.setState({ paginationData: nextProps.paginationData });
    }
  }

  componentWillUnmount() {
    OrderShipmentsStore.removeShipmentCreationListener(
      this.handleOrderShipmentCreation,
    );
    TripFormStore.removeTripAppliedEventListener(this.handleTripAppliedEvent);
  }

  getSelectedShipments = () => {
    const { selected } = this.state;
    return this.state.shipments.filter((s) => selected.includes(s._id));
  };

  handleShowBulkBookingForm = () =>
    this.setState({ showBulkBookingFormModal: true });

  handleHideBulkBookingForm = () =>
    this.setState({ showBulkBookingFormModal: false });

  handleBulkBookingSuccess = () => {
    setTimeout(() => {
      this.handleHideBulkBookingForm();
    }, 2000);
    this.props.onGetPaginatedShipments(1);
  };

  handleTripAppliedEvent = () => {
    const context = TripFormStore.getTripContext();
    const {
      formStatus: { completed, success },
    } = context;

    if (completed && success) {
      this.props.onGetPaginatedShipments(1);
    }
  };

  handleStagingShipmentsChange = (stagingShipments = []) => {
    this.updateStagingShipmentsState(
      stagingShipments.map(mapStagingShipmentItem),
    );
  };

  updateStagingShipmentsState = (nextStagingShipments) => {
    this.setState((prevState) => {
      const stagingShipments = nextStagingShipments.reduce(
        (runningList, stagedShipment) => {
          const isExpanded = prevState.expanded.includes(stagedShipment._id);
          if (isExpanded) {
            const expandedRow = cloneDeep(stagedShipment);
            expandedRow.editRow = true;
            expandedRow.expanded = true;
            return runningList.concat([stagedShipment, expandedRow]);
          }
          return runningList.concat(stagedShipment);
        },
        [],
      );

      const shipments = [...stagingShipments, ...prevState.bookedShipments];

      const updatedShipmentListState = getShipmentListState(
        shipments,
        prevState,
      );

      const stagingShipmentsLoaded = true;

      return {
        stagingShipments,
        shipments,
        ...updatedShipmentListState,
        stagingShipmentsLoaded,
      };
    });
  };

  generateTripDisabled() {
    return this.state.selected.length === 0;
  }

  handleOrderShipmentsStoreChange = () => {
    const { bookedShipments } = this.state;
    this.updateShipmentsState(bookedShipments.map(mapShipmentItem));
  };

  handleOrderShipmentCreation = (type) => {
    // Can't create a shipment while a request is in progress. When it's done the type will be 'SUCCEEDED' or 'FAILED'.
    const disableCreateShipment = type === 'REQUESTED';
    this.setState({ disableCreateShipment });
  };

  handleRemoveShipment = (selectedShipment) =>
    this.setState({
      showRemoveModal: true,
      selectedShipment,
    });

  closeEquipmentModal = () => {
    this.setState({
      showEditEquipmentModal: false,
      selectedEquipment: null,
    });
  };

  isShipmentExpanded = (id) => this.state.expanded.includes(id);

  isShipmentSelected = (id) => this.state.selected.includes(id);

  handleEditEquipment = (selectedEquipment) =>
    this.setState({
      showEditEquipmentModal: true,
      selectedEquipment,
    });

  handleSelectAllChange = (e) => {
    const {
      target: { checked: selectAll },
    } = e;

    this.setState((prevState) => {
      const { shipments } = prevState;

      let selected = [];
      if (selectAll) {
        selected = shipments.map((s) => s._id);
      }
      return {
        selected,
        selectAll,
      };
    });
  };

  handleSelectRowChange = (_id, checked) => {
    this.setState((prevState) => {
      const { shipments, selected: currentSelected } = prevState;

      let selected;
      if (checked) {
        selected = [...currentSelected, _id];
      } else {
        const index = currentSelected.findIndex((s) => s === _id);
        selected = [
          ...currentSelected.slice(0, index),
          ...currentSelected.slice(index + 1),
        ];
      }

      const selectAll = selected.length === shipments.length;
      return {
        selectAll,
        selected,
      };
    });
  };

  handleToggleExpand = (id, toggled) => {
    this.setState((prevState) => {
      const { shipments: currentShipments, expanded: currentExpanded } =
        prevState;

      let expanded;
      const shipmentIndex = currentShipments.findIndex(
        (s) => s._id === id && !s.editRow,
      );
      const summaryRow = currentShipments[shipmentIndex];

      let editRow;

      const editRowIndex = currentShipments.findIndex(
        (s) => s._id === id && s.editRow,
      );
      let shipments;
      if (editRowIndex !== -1) {
        editRow = cloneDeep(currentShipments[editRowIndex]);
        shipments = [
          ...currentShipments.slice(0, shipmentIndex),
          summaryRow,
          editRow,
          ...currentShipments.slice(shipmentIndex + 2),
        ];
      } else {
        editRow = cloneDeep(summaryRow);
        shipments = [
          ...currentShipments.slice(0, shipmentIndex),
          summaryRow,
          editRow,
          ...currentShipments.slice(shipmentIndex + 1),
        ];
      }
      editRow.editRow = true;

      if (toggled) {
        expanded = [...currentExpanded, id];

        editRow.expanded = true;
      } else {
        editRow.expanded = false;
        expanded = [
          ...currentExpanded.slice(0, shipmentIndex),
          ...currentExpanded.slice(shipmentIndex + 1),
        ];
      }

      return {
        expanded,
        shipments,
      };
    });
  };

  handleSaveEquipment = (equipment) => {
    EquipmentActionCreators.updateEquipment(equipment);

    // For Staging shipments, equipments are on the stop level. Stops in the stops array of
    // ShipmentStagingSchema aren't mapped to Mongo ids, so they don't get re-populated
    // when changes are made to an individual equipment item. The following obviously isn't
    // ideal, but with the current architecture it's the only way to make sure that equipments
    // attached to a stop in Shipment Staging get updated when their properties change.
    // We need to go through all the Staging shipments because the same piece of equipment
    // can be on multiple ones.

    if (!_.isEmpty(this.props.order.shipments_staging)) {
      const stagingShipmentsToUpdate = this.props.order.shipments_staging.map(
        (shipment) => {
          shipment.stops = shipment.stops.map((stop) => {
            stop.equipments = stop.equipments.map((eq) => {
              if (eq._id === equipment._id) {
                return equipment;
              }
              return eq;
            });
            return stop;
          });
          return shipment;
        },
      );

      updateOrder(this.props.order._id, {
        shipments_staging: stagingShipmentsToUpdate,
      });
    }
  };

  handleCloseGenerateTripModal = () => {
    this.setState({
      selected: [],
      selectAll: false,
      showGenerateTripModal: false,
    });
  };

  handleShowGenerateTripModal = () => {
    this.setState({ showGenerateTripModal: true });
  };

  handleShowTripPlannerModal = () => {
    this.setState({ showTripPlannerModal: true });
  };

  handleCloseTripPlannerModal = () => {
    this.setState({ showTripPlannerModal: false });
  };

  onHideBulkEditModal = () => this.setState({ showBulkEditModal: false });

  getDeliveryOrderRecipient = () => {
    const recipient = '';

    if (this.state.selectedShipment) {
      const shipment = this.state.shipments.filter(
        (s) => s._id === this.state.selectedShipment,
      );

      if (shipment.length) {
        return getDeliveryOrderRecipients(shipment[0].carrier);
      }
    }

    return recipient;
  };

  handleSendDeliveryOrder = (selectedShipment) =>
    this.setState({ showSendEmailModal: true, selectedShipment });

  handleGenerateDeliveryOrder = (jobId) => {
    this.setState(
      (currentState) => {
        const { shipments: currentShipments } = currentState;

        const index = currentShipments.findIndex((j) => j._id === jobId);
        if (index === -1) {
          return null;
        }
        const shipment = cloneDeep(currentShipments[index]);
        shipment.generateDeliveryOrder = true;

        const shipments = [
          ...currentShipments.slice(0, index),
          shipment,
          ...currentShipments.slice(index + 1),
        ];

        return {
          shipments,
        };
      },
      () => OrdersActionCreators.generateDeliveryOrder(jobId),
    );
  };

  handleBulkActionMenu = (bulkAction) => {
    switch (bulkAction) {
      case massUpdateActions.CHANGE_CARRIER:
        this.setState({
          bulkAction,
          showChangeCarrierModal: true,
        });
        break;
      case massUpdateActions.SEND_DELIVERY_ORDER:
        this.setState({
          bulkAction,
          showBulkUpdateSendDoModal: true,
        });
        break;
      case massUpdateActions.UPDATE_CARRIER_COST:
        this.setState({
          bulkAction,
          showBulkUpdateCarrierCostModal: true,
        });
        break;
      default:
        this.setState({
          bulkAction,
          showBulkEditModal: true,
        });
        break;
    }
  };

  updateShipmentsState(bookedShipments) {
    this.setState((prevState) => {
      const { stagingShipments } = prevState;

      const shipments = [...stagingShipments, ...bookedShipments];

      const updatedShipmentListState = getShipmentListState(
        shipments,
        prevState,
      );
      const bookedShipmentsLoaded = true;

      return {
        ...updatedShipmentListState,
        bookedShipments,
        bookedShipmentsLoaded,
        shipments,
      };
    });
  }

  sendEmail() {
    return (emails) => {
      ShipmentsActionCreators.sendDeliveryOrder(
        this.state.selectedShipment,
        emails,
      );
    };
  }

  renderBulkActionMenu() {
    const { t } = this.props;
    const disabled = !this.state.selected.length || this.props.renderedInModal;
    return (
      <DropdownMenu key="bulk-actions-menu">
        <DropdownMenuTrigger asChild>
          <UiButton variant="outline" size="sm" disabled={disabled}>
            {t('BULK_ACTIONS')}
            <ChevronDown className="ml-1 h-4 w-4" />
          </UiButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() =>
              this.handleBulkActionMenu(
                massUpdateActions.UPDATE_APPOINTMENT_WINDOW,
              )
            }
          >
            {t('UPDATE_APPOINTMENT_WINDOW')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              this.handleBulkActionMenu(massUpdateActions.EDIT_DISCHARGE)
            }
          >
            {t('UPDATE_DISCHARGE')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              this.handleBulkActionMenu(massUpdateActions.EDIT_LAST_FREE_DAY)
            }
          >
            {t('UPDATE_LFD')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              this.handleBulkActionMenu(massUpdateActions.CHANGE_CARRIER)
            }
          >
            {t('ASSIGN_CARRIER')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              this.handleBulkActionMenu(massUpdateActions.UPDATE_CARRIER_COST)
            }
          >
            {titleize(t('UPDATE_CARRIER_COST'))}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              this.handleBulkActionMenu(massUpdateActions.SEND_DELIVERY_ORDER)
            }
          >
            {t('SEND_DO')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  renderHeaderRow() {
    const { t } = this.props;
    return (
      <UiTableRow>
        <UiTableHead className="whitespace-nowrap">
          <input
            type="checkbox"
            checked={this.state.selectAll}
            onChange={this.handleSelectAllChange}
          />
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">{t('ID')}</UiTableHead>
        <UiTableHead className="whitespace-nowrap">{t('STATUS')}</UiTableHead>
        <UiTableHead className="whitespace-nowrap">
          {t('BILLING_STATUS')}
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">
          {t('EQUIPMENT_ID')}
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">
          {titleize(t('PICKUP'))}
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">
          {titleize(t('DELIVER'))}
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">
          {titleize(t('CARRIER'))}
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">
          {t('DELIVERY_ORDER')}
        </UiTableHead>
        <UiTableHead className="whitespace-nowrap">&nbsp;</UiTableHead>
      </UiTableRow>
    );
  }

  renderShipments() {
    const { bookedShipmentsLoaded, stagingShipmentsLoaded, shipments } =
      this.state;
    const { t } = this.props;
    if (!bookedShipmentsLoaded && !stagingShipmentsLoaded) {
      return (
        <tr>
          <td colSpan="6" className="p-2">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {t('LOADING_SHIPMENTS')}...
          </td>
        </tr>
      );
    }

    if (shipments.length === 0) {
      return (
        <tr>
          <td colSpan="6" className="p-2">
            <h4 className="text-base text-grey-dark">
              {t('NO_SHIPMENTS_CLICK_TO_ADD_MORE')}
            </h4>
          </td>
        </tr>
      );
    }

    return this.state.shipments.map((shipment) => (
      <OrderShipment
        order={this.props.order}
        shipment={shipment}
        selected={this.isShipmentSelected(shipment._id)}
        expanded={this.isShipmentExpanded(shipment._id)}
        onSelectRowChange={this.handleSelectRowChange}
        onEditEquipment={this.handleEditEquipment}
        onGenerateDeliveryOrder={this.handleGenerateDeliveryOrder}
        onSendDeliveryOrder={this.handleSendDeliveryOrder}
        onRemoveShipment={this.handleRemoveShipment}
        onToggleExpand={this.handleToggleExpand}
        key={getKey(shipment)}
      />
    ));
  }

  renderTripPlannerButtons = () => {
    if (!SessionStore.hasTripPlannerRole()) {
      return [];
    }
    const { t } = this.props;
    return [
      <UiButton
        key="create-trip-button"
        variant="outline"
        size="sm"
        onClick={this.handleShowTripPlannerModal}
      >
        {t('PLAN_A_TRIP')}
      </UiButton>,
      <UiButton
        key="generate-trip-button"
        variant="outline"
        size="sm"
        disabled={this.generateTripDisabled()}
        onClick={this.handleShowGenerateTripModal}
      >
        {t('GENERATE_A_TRIP')}
      </UiButton>,
    ];
  };

  renderBulkBookingButton() {
    const { t } = this.props;

    return [
      <UiButton
        key="bulk-booking-button"
        variant="outline"
        size="sm"
        onClick={this.handleShowBulkBookingForm}
      >
        {t('BOOK_MULTIPLE_SHIPMENTS')}
      </UiButton>,
    ];
  }

  renderBulkActionsButton = () => {
    return [this.renderBulkActionMenu()];
  };

  renderShipmentActions = () => {
    return [
      ...this.renderTripPlannerButtons(),
      ...this.renderBulkBookingButton(),
      ...this.renderBulkActionsButton(),
    ];
  };

  onPaginationSelect = (eventKey) =>
    this.props.onGetPaginatedShipments(eventKey);

  renderPagination() {
    const { paginationData } = this.state;
    const totalPages = (paginationData && paginationData.totalPages) || 1;
    const currentPage = (paginationData && paginationData.currentPage) || 1;
    const perPage = 25;
    return (
      <PaginationV2
        page={currentPage}
        perPage={perPage}
        total={totalPages * perPage}
        showCount={false}
        onPageClick={this.onPaginationSelect}
      />
    );
  }

  render() {
    const {
      order: { carrier: orderCarrier },
      t,
    } = this.props;
    const shipperId =
      this.props.order?.shipper?._id || this.props.order?.shipper?.id;
    const defaultDeliveryOrderRecipient = this.getDeliveryOrderRecipient();
    const carrierAuthorized = orderCarrier && orderCarrier.authorized;
    const carrier = carrierAuthorized ? orderCarrier : null;

    return (
      <CargomaticCollapsibleCard
        id="order-shipments"
        header={() => (
          <div
            data-testid="no-leg-management-order-shipments"
            className="flex flex-row flex-wrap items-center justify-between w-full"
          >
            <div className="flex items-center gap-3">
              <span>{titleize(t('SHIPMENT', { count: 2 }))}</span>
              {this.state.accountingHold && (
                <CargomaticBadge color="red">
                  {t('ACCOUNT_ON_HOLD')}
                </CargomaticBadge>
              )}
              <UiButton
                id="order-add-shipment-button"
                size="sm"
                variant="default"
                onClick={() => {
                  createShipment(this.props, {
                    shipment_type: 'drayage',
                    no_equipment: true,
                  });
                }}
                disabled={
                  this.state.accountingHold || this.state.disableCreateShipment
                }
              >
                <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />
                {titleize(t('ADD'))}
              </UiButton>
            </div>
            <div className="flex flex-wrap items-start gap-2">
              {this.renderShipmentActions()}
            </div>
          </div>
        )}
      >
        {this.state.showGenerateTripModal && (
          <GenerateTripModal
            show={this.state.showGenerateTripModal}
            onClose={this.handleCloseGenerateTripModal}
            onGenerateTrip={this.handleGenerateTrip}
            orderId={this.props.order._id}
            shipments={this.getSelectedShipments()}
          />
        )}
        {this.state.showTripPlannerModal && (
          <TripPlannerModal
            show={this.state.showTripPlannerModal}
            onClose={this.handleCloseTripPlannerModal}
            shipperId={shipperId}
            orderId={this.props.order._id}
            carrier={carrier}
            equipments={this.props.order.equipments}
          />
        )}
        {this.state.showRemoveModal && (
          <RemoveShipmentModal
            show={this.state.showRemoveModal}
            onClose={() => {
              this.setState({
                showRemoveModal: false,
                selectedShipment: null,
              });
            }}
            order={this.props.order}
            _id={this.state.selectedShipment}
          />
        )}
        {this.state.showEditEquipmentModal && (
          <EditEquipmentModal
            show={this.state.showEditEquipmentModal}
            onHide={this.closeEquipmentModal}
            equipment={this.state.selectedEquipment}
            onSave={this.handleSaveEquipment}
          />
        )}
        {this.state.showBulkEditModal && (
          <BulkEditModal
            show
            action={this.state.bulkAction}
            onHide={this.onHideBulkEditModal}
            shipments={this.state.selected}
            origin={this.props.order.arriving[0]}
            destination={this.props.order.delivering[0]}
          />
        )}
        {this.state.showChangeCarrierModal && (
          <ChangeUserModal
            jobs={this.getSelectedShipments()}
            show={this.state.showChangeCarrierModal}
            type="carrier"
            onSave={ShipmentsActionCreators.updateCarrier}
            onAssignBrokerCarrier={(shipment, driver) =>
              this.setState({
                showConfirmAssignCarrierModal: true,
                showChangeCarrierModal: false,
                brokerCarrierDriver: driver,
              })
            }
            onHide={() => this.setState({ showChangeCarrierModal: false })}
          />
        )}
        <ConfirmAssignCarrierModal
          isOpen={this.state.showConfirmAssignCarrierModal}
          brokerCarrierDriver={this.state.brokerCarrierDriver}
          shipment={this.getSelectedShipments()}
          onConfirm={(shipments, carrier) => {
            ShipmentsActionCreators.updateCarrier(carrier, shipments);
          }}
          onClose={() =>
            this.setState({ showConfirmAssignCarrierModal: false })
          }
        />
        {this.state.showBulkUpdateCarrierCostModal && (
          <BulkUpdateCarrierCostModal
            title={t('UPDATE_CARRIER_COSTS')}
            show={this.state.showBulkUpdateCarrierCostModal}
            shipments={this.state.selected}
            onHide={() => {
              this.setState({ showBulkUpdateCarrierCostModal: false });
            }}
          />
        )}
        {this.state.showBulkUpdateSendDoModal && (
          <BulkUpdateSendDOModal
            title={titleize(t('SEND_DELIVERY_ORDER'))}
            send_do={
              this.state.bulkAction === massUpdateActions.SEND_DELIVERY_ORDER
            }
            show={this.state.showBulkUpdateSendDoModal}
            shipments={this.state.selected}
            onHide={() => this.setState({ showBulkUpdateSendDoModal: false })}
          />
        )}
        <div className="overflow-x-auto">
          <UiTable>
            <UiTableHeader>{this.renderHeaderRow()}</UiTableHeader>
            <UiTableBody>{this.renderShipments()}</UiTableBody>
          </UiTable>
          <div className="flex flex-row-reverse pr-6">
            {this.renderPagination()}
          </div>
        </div>
        {this.state.showSendEmailModal && (
          <SendDOEmailModal
            show={this.state.showSendEmailModal}
            onSend={this.sendEmail()}
            onHide={() => this.setState({ showSendEmailModal: false })}
            defaultRecipient={defaultDeliveryOrderRecipient}
          />
        )}
        {this.state.showBulkBookingFormModal && (
          <BulkBookingForm
            orderId={this.props.order._id}
            order={this.props.order}
            show={this.state.showBulkBookingFormModal}
            onHide={this.handleHideBulkBookingForm}
            onSaveSuccess={this.handleBulkBookingSuccess}
            origin={this.props.order.arriving[0]}
            destination={this.props.order.delivering[0]}
            shipperId={shipperId}
            carrier={carrier}
          />
        )}
      </CargomaticCollapsibleCard>
    );
  }
}

class OrderShipment extends Component {
  static propTypes = {
    onEditEquipment: PropTypes.func.isRequired,
    onGenerateDeliveryOrder: PropTypes.func.isRequired,
    onSendDeliveryOrder: PropTypes.func.isRequired,
    onRemoveShipment: PropTypes.func.isRequired,
    onSelectRowChange: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    selected: PropTypes.bool,
    order: PropTypes.object.isRequired,
    shipment: PropTypes.shape({
      isStaging: PropTypes.bool.isRequired,
      _id: PropTypes.string.isRequired,
    }).isRequired,
    expanded: PropTypes.bool,
  };

  static defaultProps = {
    selected: false,
    expanded: false,
  };

  handleGenerateDeliveryOrder = (shipmentId) =>
    this.props.onGenerateDeliveryOrder(shipmentId);

  handleSendDeliveryOrder = (shipmentId) =>
    this.props.onSendDeliveryOrder(shipmentId);

  handleToggleExpand = () =>
    this.props.onToggleExpand(this.props.shipment._id, !this.props.expanded);

  handleRemoveShipment = () =>
    this.props.onRemoveShipment(this.props.shipment._id);

  handleEditEquipment = (equipmentId) => {
    const {
      shipment: { equipments },
    } = this.props;

    const equipment = equipments.find((eq) => eq._id === equipmentId);
    this.props.onEditEquipment(equipment);
  };

  handleCheckboxChange = (e) => {
    const {
      target: { checked },
    } = e;
    const {
      shipment: { _id },
    } = this.props;

    this.props.onSelectRowChange(_id, checked);
  };

  handleCancelEdit = () =>
    this.props.onToggleExpand(this.props.shipment._id, false);

  renderActions() {
    const { shipment } = this.props;

    const { isStaging } = shipment;

    if (isStaging) {
      return (
        <StagingActions
          onRemoveShipment={this.handleRemoveShipment}
          onToggleExpand={this.handleToggleExpand}
        />
      );
    }

    return (
      <ManageButton
        shipment={shipment}
        onGenerateDeliveryOrder={this.handleGenerateDeliveryOrder}
        onSendDeliveryOrder={this.handleSendDeliveryOrder}
      />
    );
  }

  renderRow() {
    const { selected, shipment } = this.props;

    const { status, billing_status } = shipment;

    return (
      <Disclosure
        as="tr"
        className="border-b transition-colors hover:bg-muted/50"
      >
        <UiTableCell className="whitespace-nowrap">
          <input
            type="checkbox"
            checked={selected}
            onChange={this.handleCheckboxChange}
          />
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {formatId(shipment)}
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {formatStatus(status)}
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {formatBillingStatus(billing_status)}
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          <EquipmentsColumn
            equipments={shipment.equipments}
            onEditEquipment={this.handleEditEquipment}
          />
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {formatLocation(shipment.pickup)}
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {formatLocation(shipment.deliver)}
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {formatCarrier(shipment)}
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          <DeliveryOrderColumnWithLocalization shipment={shipment} />
        </UiTableCell>
        <UiTableCell className="whitespace-nowrap">
          {this.renderActions()}
        </UiTableCell>
      </Disclosure>
    );
  }

  renderExpandedRow() {
    const { order, shipment } = this.props;

    return (
      <tr>
        <td colSpan="10">
          <ManageStagingItem
            order={order}
            shipment={shipment}
            cancelEditing={this.handleCancelEdit}
          />
        </td>
      </tr>
    );
  }

  render() {
    const {
      shipment: { editRow },
      expanded,
    } = this.props;

    if (editRow) {
      return expanded ? this.renderExpandedRow() : null;
    }

    return this.renderRow();
  }
}

function getKey(shipment) {
  const { isStaging, _id, editRow } = shipment;

  const rowTypeLabel = editRow ? 'expanded' : 'summary';
  return `shipment-${_id}-${isStaging}-${rowTypeLabel}`;
}

function formatId(shipment) {
  const { isStaging, shipment_id } = shipment;

  if (isStaging) {
    return null;
  }

  const link = `/shipments/${shipment._id}`;
  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      {shipment_id}
    </a>
  );
}

function formatCarrier(shipment) {
  const { carrier } = shipment;
  if (!carrier) {
    return null;
  }
  const carrierId = carrier._id ? carrier._id : carrier;
  const link = `/users/${carrierId}`;
  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      {unescapeHTML(carrier.name)}
    </a>
  );
}

function formatStatus(cell) {
  return <ShipmentStatusBadge status={cell} />;
}

function formatBillingStatus(cell) {
  if (!cell) return null;
  return <BillingStatusBadge billingStatus={cell} />;
}

function formatLocation(cell) {
  if (!cell) {
    return null;
  }

  return (
    <div>
      <div>
        {cell.name && (
          <label className="mr-2 text-sm font-bold text-grey-darker">
            {unescapeHTML(cell.name)}
          </label>
        )}
      </div>
      <span className="mb-4">{cell.address}</span>
    </div>
  );
}

class DeliveryOrderColumn extends Component {
  static propTypes = {
    shipment: PropTypes.object.isRequired,
  };

  openDeliveryOrder = (shipment) => async (event) => {
    const { t } = this.props;
    event.preventDefault();
    const { data, errors: documentErrors } = await getDocumentsV2(
      [shipment._id],
      {
        types: 'delivery_order',
        options: {
          order: {
            field: 'createdAt',
            direction: 'desc',
          },
          limit: 1,
        },
      },
    );

    if (documentErrors) {
      showToast(
        t('GET_DOCUMENT_ERROR'),
        t('GET_DOCUMENT_FAILED_WITH_ERROR', {
          error: documentErrors[0].message,
        }),
        'error',
      );
      return;
    }

    if (data?.getDocumentsV2?.documents?.length > 0) {
      const { data: signedUrl, errors: signedUrlErrors } = await getSignedUrl(
        data.getDocumentsV2.documents[0].key,
      );

      if (signedUrlErrors) {
        showToast(
          t('GET_SIGNED_URL_ERROR'),
          t('GET_SIGNED_URL_FAILED_WITH_ERROR', {
            error: signedUrlErrors[0].message,
          }),
          'error',
        );
        return;
      }

      if (signedUrl?.getSignedUrl?.signedUrl) {
        openSignedUrlInNewTab(signedUrl.getSignedUrl.signedUrl);
      }
    }
  };

  render() {
    const { shipment, t } = this.props;
    const { generateDeliveryOrder, delivery_order_sent, carrier } = shipment;

    if (!carrier) {
      return null;
    }

    return (
      <div className="flex items-center gap-1">
        {generateDeliveryOrder ? (
          <UiButton variant="ghost" size="icon" className="h-8 w-8" disabled>
            <Loader2 className="h-4 w-4 animate-spin" />
          </UiButton>
        ) : (
          <UiButton
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={t('DOWNLOAD')}
            onClick={this.openDeliveryOrder(shipment)}
          >
            <Download className="h-4 w-4" />
          </UiButton>
        )}
        {delivery_order_sent && (
          <BadgeAlt
            variant="outline"
            className="bg-green-100 text-green-800 border-green-300 text-xs"
          >
            {t('SENT')}
          </BadgeAlt>
        )}
      </div>
    );
  }
}

const DeliveryOrderColumnWithLocalization =
  withTranslation()(DeliveryOrderColumn);

class EquipmentsColumn extends Component {
  static propTypes = {
    equipments: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        equipment_id: PropTypes.string,
      }),
    ),
    onEditEquipment: PropTypes.func.isRequired,
  };

  static defaultProps = {
    equipments: [],
  };

  handleClick = (equipmentId) => (e) => {
    e.preventDefault();
    this.props.onEditEquipment(equipmentId);
  };

  render() {
    const { equipments } = this.props;

    if (!equipments || equipments.length === 0) {
      return null;
    }
    return (
      <div>
        {equipments.map((equipment) => (
          <a
            key={equipment._id}
            href="#"
            className="block mb-2"
            onClick={this.handleClick(equipment._id)}
          >
            {equipment.equipment_id}
          </a>
        ))}
      </div>
    );
  }
}

class ManageButton extends Component {
  static propTypes = {
    shipment: PropTypes.shape({
      _id: PropTypes.string,
    }),
    onGenerateDeliveryOrder: PropTypes.func.isRequired,
    onSendDeliveryOrder: PropTypes.func.isRequired,
  };

  render() {
    const { shipment } = this.props;
    const { _id, carrier } = shipment;

    return (
      <Translation>
        {(t) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <UiButton variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </UiButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {carrier && isIntermodalShipmentType(shipment) && (
                <DropdownMenuItem
                  onClick={() => this.props.onSendDeliveryOrder(_id)}
                >
                  {t('EMAIL_DELIVERY_ORDER')}
                </DropdownMenuItem>
              )}
              {carrier && (
                <DropdownMenuItem
                  onClick={() => this.props.onGenerateDeliveryOrder(_id)}
                >
                  {t('GENERATE_DELIVERY_ORDER')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => window.open(`/shipments/${_id}`, '_blank')}
              >
                {t('VIEW_DETAILS')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </Translation>
    );
  }
}

class StagingActions extends Component {
  static propTypes = {
    onRemoveShipment: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
  };

  handleToggleExpand = () => this.props.onToggleExpand();

  handleRemoveShipment = () => this.props.onRemoveShipment();

  render() {
    return (
      <Translation>
        {(t) => (
          <div className="flex flex-row">
            <Disclosure.Button
              className="mr-2 text-white button bg-blue-cm hover:bg-blue-cm-darker"
              onClick={this.handleToggleExpand}
            >
              {titleize(t('EDIT'))}
            </Disclosure.Button>

            <button
              className="text-white button bg-grey-dark hover:bg-grey-darker"
              onClick={this.handleRemoveShipment}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </Translation>
    );
  }
}

function getShipmentListState(shipments, prevState) {
  const shipmentIds = shipments.map((s) => s._id);
  const selected = prevState.selected.filter((id) => shipmentIds.includes(id));
  const expanded = prevState.expanded.filter((id) => shipmentIds.includes(id));
  const selectAll = selected.length === shipmentIds.length;

  return {
    selected,
    expanded,
    selectAll,
  };
}

export default withTranslation()(ShipmentsList);
